import React from 'react';
import { useTranslation } from 'react-i18next';
import useFullPageQuery from '../../hooks/useFullPageQuery';
import { filterByTypename } from '../../utils/graphql';
import { InMemoryCharitiesListQuery } from '../../../types/__generated__';
import PageSection, { PageSectionProps } from '../PageSection/PageSection';
import styles from './InMemoryCharitiesList.scss';
import classnames from 'classnames';
import LoadMoreButton from '../LoadMoreButton/LoadMoreButton';
import { NetworkStatus } from '@apollo/client';
import clientConfig from '../../client/config';
import DOCUMENT from './InMemoryCharitiesList.gql';
import AboutRelationshipMiniSkeleton from '../AboutRelationship/AboutRelationshipSkeleton';
import AboutRelationship from '../AboutRelationship/AboutRelationship';

export const InMemoryCharitiesList: React.FunctionComponent = () => {
  const { t } = useTranslation('common', { useSuspense: false });

  const { data, error, fetchMore, networkStatus, refetch } = useFullPageQuery<
    InMemoryCharitiesListQuery
  >(DOCUMENT, {
    notifyOnNetworkStatusChange: true,
    variables: {
      first: 3,
    },
  });

  const pageSectionProps: PageSectionProps = {
    title: t('inMemory.charities.titleNoName'),
    hasBorder: true,
    className: styles.container,
  };

  if (networkStatus === NetworkStatus.loading) {
    return (
      <PageSection {...pageSectionProps}>
        <AboutRelationshipMiniSkeleton
          animated
          displayRefreshButton={false}
          inline
        />
      </PageSection>
    );
  }

  if (error) {
    return (
      <PageSection {...pageSectionProps}>
        <AboutRelationshipMiniSkeleton
          onRefresh={refetch}
          refreshButtonLabel={t('inMemory.charities.refresh')}
          inline
        />
      </PageSection>
    );
  }

  if (!data?.page?.relationships) {
    return null;
  }

  const {
    slug,
    inMemory,
    relationships: {
      beneficiaries: {
        edges,
        pageInfo: { hasNextPage, endCursor },
      },
    },
  } = data.page;

  if (inMemory?.firstNameOfDeceased) {
    pageSectionProps.title = `${t('inMemory.charities.titleWithName')} ${
      inMemory.firstNameOfDeceased
    }`;
  }

  const createFrpUrl = `${clientConfig.baseUri}/fundraising-page/creation/initiate?rmpId=${slug}`;

  const beneficiaryNodes = edges.map(edge => {
    return { ...edge.node };
  });

  const charities = filterByTypename(beneficiaryNodes, 'Charity');

  if (charities.length === 0) {
    // NOTE: As an IMO page is a by-product of FRP creation, there should always be at least one related charity - so this is an edge case.
    return (
      <PageSection {...pageSectionProps}>
        <p className="jg-space-mbn" data-qa="no-charities">
          {t('inMemory.charities.noCharitiesMessage')}{' '}
          <a href={createFrpUrl}>{t('inMemory.charities.createFrpLink')}</a>
        </p>
      </PageSection>
    );
  }

  async function handleFetchMore() {
    await fetchMore({
      variables: {
        first: 2,
        after: endCursor,
      },
    });
  }

  return (
    <PageSection {...pageSectionProps}>
      <ul data-qa="imo-charities-list" className={styles.list_wrapper}>
        {charities.map(charity => {
          const { id, logo, name, description, profilePageUrl } = charity;
          return (
            <li className={styles.list_item} key={id}>
              <AboutRelationship
                logo={logo}
                name={name ?? ''}
                landingPageUrl={profilePageUrl}
                description={description}
                inline
              />
            </li>
          );
        })}
      </ul>
      {hasNextPage && (
        <div className={styles.button_wrapper}>
          <LoadMoreButton
            className={classnames('jg-btn', styles.button)}
            loading={networkStatus === NetworkStatus.fetchMore}
            onClick={handleFetchMore}
            trackingData={{
              action_name: 'click',
              subtype: 'button',
              event: 'click',
              event_value: 'show more',
              sub_section: 'campaign members',
            }}
            trackingIdentifier="in-memory-show-more-charities-button"
          >
            {t('inMemory.charities.showMoreCharities')}
          </LoadMoreButton>
        </div>
      )}
    </PageSection>
  );
};
